import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiRequestError } from '../../../lib/api'
import { useOnboardingV2Store } from '../../../stores/onboardingV2Store'
import OnboardingLayout from './OnboardingLayout'
import PrimaryButton from './PrimaryButton'
import SecondaryButton from './SecondaryButton'
import ReviewRoleCard from './ReviewRoleCard'
import { stepsForMode } from './steps'

interface DiscardedItem {
  proofId: string
  label: string
}

export default function ReviewScreen() {
  const navigate = useNavigate()
  const capabilityMode = useOnboardingV2Store((s) => s.state?.capability_mode ?? 'managed')
  const review = useOnboardingV2Store((s) => s.review)
  const fetchReview = useOnboardingV2Store((s) => s.fetchReview)
  const editProof = useOnboardingV2Store((s) => s.editProof)
  const discardProof = useOnboardingV2Store((s) => s.discardProof)
  const restoreProof = useOnboardingV2Store((s) => s.restoreProof)
  const answerQuestion = useOnboardingV2Store((s) => s.answerQuestion)
  const skipQuestion = useOnboardingV2Store((s) => s.skipQuestion)
  const commit = useOnboardingV2Store((s) => s.commit)

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [discarded, setDiscarded] = useState<DiscardedItem[]>([])
  const [committing, setCommitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    fetchReview().catch(() => setLoadError(true))
  }, [fetchReview])

  const toggle = (roleKey: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(roleKey)) next.delete(roleKey)
      else next.add(roleKey)
      return next
    })

  const handleDiscard = async (proofId: string) => {
    // Capture a label before the proof drops out of the refetched review.
    const proof = review?.roles.flatMap((r) => r.proofs).find((p) => p.proof_id === proofId)
    const label = proof
      ? `${proof.employer} · ${proof.canonical_description.slice(0, 60)}${proof.canonical_description.length > 60 ? '…' : ''}`
      : 'Discarded proof'
    await discardProof(proofId)
    setDiscarded((d) => [...d.filter((x) => x.proofId !== proofId), { proofId, label }])
  }

  const handleRestore = async (proofId: string) => {
    await restoreProof(proofId)
    setDiscarded((d) => d.filter((x) => x.proofId !== proofId))
  }

  const handleCommit = async (skipReview: boolean) => {
    setCommitting(true)
    setError(null)
    try {
      const result = await commit(skipReview)
      navigate(result.library_url || '/library', {
        replace: true,
        state: { proofsAdded: result.proofs_added },
      })
    } catch (err) {
      // 409 = the session is already committed (e.g. a retried/duplicate
      // submit, or it succeeded but the response was lost). The proofs are in
      // the library — treat it as success rather than stranding the user here.
      if (err instanceof ApiRequestError && err.status === 409) {
        navigate('/library', { replace: true })
        return
      }
      setError(err instanceof ApiRequestError ? err.error.message : 'Could not add your proofs.')
      setCommitting(false)
    }
  }

  const steps = stepsForMode(capabilityMode)

  if (loadError) {
    return (
      <OnboardingLayout steps={steps} activeKey="review">
        <div className="mx-auto max-w-[680px] text-center">
          <p className="font-body text-ob-error">We couldn't load your review. Refresh to try again.</p>
        </div>
      </OnboardingLayout>
    )
  }

  if (!review) {
    return (
      <OnboardingLayout steps={steps} activeKey="review">
        <div className="flex justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-ob-secondary/30 border-t-ob-ink" />
        </div>
      </OnboardingLayout>
    )
  }

  const noProofs = review.total_proofs === 0 && review.roles.length === 0

  return (
    <OnboardingLayout steps={steps} activeKey="review">
      <div className="mx-auto w-full max-w-[680px]">
        <header className="mb-8 text-center">
          <h1 className="font-serif text-[28px] font-semibold text-ob-ink md:text-3xl">
            Review what we found
          </h1>
          {!noProofs && (
            <p className="mt-2 font-body text-base text-ob-secondary">
              We organised what we read into {review.total_roles}{' '}
              {review.total_roles === 1 ? 'role' : 'roles'} with {review.total_proofs}{' '}
              {review.total_proofs === 1 ? 'proof' : 'proofs'}. Have a look — leave anything that's
              right, fix or discard anything that's not.
            </p>
          )}
        </header>

        {noProofs ? (
          <div className="border border-ob-secondary/20 bg-ob-surface-card p-6 text-center">
            <p className="font-body text-ob-on-surface">
              We couldn't pull any clear proof from what you shared.
            </p>
            <p className="mt-2 font-body text-sm text-ob-secondary">
              You can head to your Library and add accomplishments manually.
            </p>
            <div className="mt-6 flex justify-center">
              <PrimaryButton disabled={committing} onClick={() => void handleCommit(true)}>
                {committing ? 'One moment…' : 'Go to Library'}
              </PrimaryButton>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {review.roles.map((role) => (
                <ReviewRoleCard
                  key={role.role_key}
                  role={role}
                  expanded={expanded.has(role.role_key)}
                  onToggle={() => toggle(role.role_key)}
                  onEditProof={(id, desc) => editProof(id, { canonical_description: desc })}
                  onDiscardProof={handleDiscard}
                  onAnswerQuestion={answerQuestion}
                  onSkipQuestion={skipQuestion}
                />
              ))}
            </div>

            {discarded.length > 0 && (
              <div className="mt-6 border border-ob-secondary/20 bg-ob-surface-card p-4">
                <p className="mb-2 font-label text-[11px] uppercase tracking-wider text-ob-secondary">
                  Discarded ({discarded.length})
                </p>
                <ul className="space-y-2">
                  {discarded.map((item) => (
                    <li key={item.proofId} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate font-body text-sm text-ob-secondary line-through">
                        {item.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => void handleRestore(item.proofId)}
                        className="shrink-0 font-body text-sm font-semibold text-ob-ink hover:text-ob-ink/70"
                      >
                        Undo
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {error && (
              <p className="mt-4 text-center font-body text-sm text-ob-error" role="alert">
                {error}
              </p>
            )}

            {/* Sticky action bar — spans the layout width */}
            <div className="sticky bottom-0 -mx-4 mt-8 border-t border-ob-secondary/20 bg-ob-surface px-4 py-4 md:-mx-16 md:px-16">
              <div className="mx-auto flex max-w-[680px] flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <SecondaryButton disabled={committing} onClick={() => void handleCommit(true)}>
                  Skip review — add all
                </SecondaryButton>
                <PrimaryButton
                  disabled={committing || !review.can_commit_primary}
                  title={
                    review.can_commit_primary
                      ? undefined
                      : 'Resolve the highlighted questions first, or use “Skip review”.'
                  }
                  onClick={() => void handleCommit(false)}
                >
                  {committing
                    ? 'Adding…'
                    : `Add ${review.total_proofs} ${review.total_proofs === 1 ? 'proof' : 'proofs'} to Library →`}
                </PrimaryButton>
              </div>
            </div>
          </>
        )}
      </div>
    </OnboardingLayout>
  )
}
